import { schAloftPrice } from '@aloft/toolkit-shared';
import { Text, View } from '@react-pdf/renderer';

import { COMMENTARY_TEXT, getMethodologyText } from '../../commentary/consts.ts';
import { FlexRow } from '../components/FlexRow.tsx';
import { List } from '../components/List.tsx';
import { Tkv2PDFPage } from '../components/Tkv2PDFPage.tsx';
import { fontStyles, gridStyles } from '../styles.ts';
import { MethodologyPageProps } from '../types.ts';

export const MethodologyPage = ({
  subjectAddress,
  propertyType,
  appraiserFee,
  amcFee,
  hasArvOption,
}: MethodologyPageProps) => {
  const { title } = COMMENTARY_TEXT.methodology;

  if (hasArvOption) {
    return (
      <>
        <Tkv2PDFPage title={title} address={subjectAddress}>
          {/* About This Report */}
          <Text style={fontStyles.h4}>About This Report</Text>
          <Text style={fontStyles.body}>
            This appraisal has been prepared and reported in accordance with the Uniform Standards
            of Professional Appraisal Practice (USPAP), 2024 edition, under Standards Rule 2-2(b).
          </Text>
          <Text style={fontStyles.body}>
            The appraisal was developed through the Appraisify Hybrid Platform for the purpose of
            asset valuation involving an investment lending product. This report utilizes verified
            third-party inspection data regarding the subject&apos;s current &quot;As-Is&quot;
            condition, alongside a
            <Text style={fontStyles.bodyBold}>&nbsp;client-provided Scope of Work (SOW)&nbsp;</Text>
            detailing proposed renovations to derive an &quot;After-Repair Value&quot; (ARV).
          </Text>
          <Text style={fontStyles.body}>
            The appraiser did not perform a personal interior inspection. All physical
            characteristics, condition data, and photos of the current state were obtained from
            verified third-party sources.
          </Text>

          {/* Scope of Work and Reporting Process */}
          <Text style={fontStyles.h4}>Scope of Work and Reporting Process</Text>
          <List
            type="bullet"
            items={[
              <Text key="property-identification" style={fontStyles.body}>
                <Text style={fontStyles.bodyBold}>Property Identification:&nbsp;</Text>
                The subject was identified by street address, parcel number, and GIS mapping tools
                verified through the Appraisify platform.
              </Text>,
              <Text key="inspection" style={fontStyles.body}>
                <Text style={fontStyles.bodyBold}>Inspection:&nbsp;</Text>
                The appraiser relied on a third-party property data report (photo and sketch
                verified) to establish the &quot;As-Is&quot; condition.
              </Text>,
              <Text key="renovation-analysis" style={fontStyles.body}>
                <Text style={fontStyles.bodyBold}>Renovation Analysis:&nbsp;</Text>
                The appraiser reviewed the Client-Provided Scope of Work (SOW) and/or renovation
                budget. The analysis assumes these improvements will be completed in a workmanlike
                manner and consistent with local market standards.
              </Text>,
              <Text key="data-sources" style={fontStyles.body}>
                <Text style={fontStyles.bodyBold}>Data Sources:&nbsp;</Text>
                Data were obtained from MLS, public record, tax assessor data, flood maps, surveys,
                satellite imagery, proprietary Appraisify analytics, and the specific renovation
                SOW/Budget provided by the client.
              </Text>,
              <Text key="approaches-to-value" style={fontStyles.body}>
                <Text style={fontStyles.bodyBold}>Approaches to Value:&nbsp;</Text>
                Sales Comparison: Primary method. Comparable sales were selected based on their
                similarity to the subject&apos;s hypothetical &quot;As-Repaired&quot; condition, or
                adjusted accordingly for the value of the proposed improvements. Cost Approach:
                Applied where applicable using Craftsman cost data, inclusive of the proposed
                renovation costs. Income Approach: Omitted unless specifically requested or relevant
                to the investment income potential of the subject. Adjustments: Location and
                physical adjustments were derived from paired-sales analysis. Specific adjustments
                for condition and quality of construction reflect the subject property as if the
                proposed renovations were complete.
              </Text>,
            ]}
          />

          {/* Definition of Market Value */}
          <Text style={fontStyles.h4}>Definition of Market Value</Text>
          <Text style={fontStyles.body}>
            The definition of Market Value conforms to 12 CFR Part 34, Subpart C – Appraisals.
          </Text>

          {/* Important Note Regarding Value */}
          <Text style={fontStyles.h4}>Important Note Regarding Value</Text>
          <Text style={fontStyles.body}>
            The value opinion reported herein is an After-Repair Value (ARV). It represents the
            probable price the property should bring in a competitive market subject to the
            hypothetical condition that the renovations detailed in the client&apos;s SOW have been
            completed as of the effective date of the appraisal.
          </Text>
          <Text style={fontStyles.body}>
            (Optional: If reporting As-Is Value as well) If an &quot;As-Is&quot; Value (AIV) is also
            reported, it represents the value of the property in its current physical condition
            prior to the commencement of the proposed SOW.
          </Text>

          {/* Intended Use and User */}
          <Text style={fontStyles.h4}>Intended Use and User</Text>
          <Text style={fontStyles.body}>
            The intended use of this report is for collateral evaluation, risk management, and
            credit decisioning for investment lending / renovation financing. The intended user is
            the client named in this report and its authorized assigns. Any other use by unintended
            parties is prohibited.
          </Text>
        </Tkv2PDFPage>

        <Tkv2PDFPage title={title} address={subjectAddress}>
          {/* Statement of Assumptions and Limiting Conditions */}
          <Text style={fontStyles.h4}>Statement of Assumptions and Limiting Conditions</Text>
          <List
            type="bullet"
            items={[
              <Text key="title" style={fontStyles.body}>
                <Text style={fontStyles.bodyBold}>Title:&nbsp;</Text>
                The appraiser assumes that the title is good and marketable unless he or she becomes
                aware of information to the contrary.
              </Text>,
              <Text key="sketches" style={fontStyles.body}>
                <Text style={fontStyles.bodyBold}>Sketches:&nbsp;</Text>
                If a sketch is included, it illustrates approximate dimensions.
              </Text>,
              <Text key="third-party-data" style={fontStyles.body}>
                <Text style={fontStyles.bodyBold}>Third-Party Data:&nbsp;</Text>
                The appraiser has relied on data provided by third parties, including the
                third-party inspector and the Client-Provided Scope of Work. The appraiser assumes
                there are no material omissions and makes no guarantees regarding their accuracy.
              </Text>,
              <Text key="renovation-scope" style={fontStyles.body}>
                <Text style={fontStyles.bodyBold}>Renovation Scope:&nbsp;</Text>
                The appraiser has relied on the specific line items, descriptions, and materials
                outlined in the client-provided SOW. The appraiser is not a contractor or building
                inspector. It is assumed that the SOW is adequate to bring the property to the
                proposed condition and that all work will be professionally completed to code. The
                appraiser assumes no responsibility for hidden defects that may be discovered during
                the renovation process that would alter the SOW or cost to cure.
              </Text>,
              <Text key="legal" style={fontStyles.body}>
                <Text style={fontStyles.bodyBold}>Legal:&nbsp;</Text>
                The appraiser will not be required to give testimony or appear in court unless
                specific arrangements have been made.
              </Text>,
            ]}
          />

          {/* Assignment Conditions */}
          <Text style={fontStyles.h4}>Assignment Conditions</Text>
          <List
            type="bullet"
            items={[
              <Text key="interest-appraised" style={fontStyles.body}>
                <Text style={fontStyles.bodyBold}>Interest Appraised:&nbsp;</Text>
                Fee Simple Estate.
              </Text>,
              <View key="extraordinary-assumptions">
                <Text style={fontStyles.body}>
                  <Text style={fontStyles.bodyBold}>Extraordinary Assumptions:&nbsp;</Text>
                </Text>
                <View style={{ marginLeft: 12 }}>
                  <FlexRow alignItems="flex-start">
                    <Text style={fontStyles.body}>a.&nbsp;</Text>
                    <Text style={fontStyles.body}>
                      The physical characteristics and condition of the property
                      (&quot;As-Is&quot;), as reported by the third-party inspection, are accurate.
                    </Text>
                  </FlexRow>
                  <FlexRow alignItems="flex-start">
                    <Text style={fontStyles.body}>b.&nbsp;</Text>
                    <Text style={fontStyles.body}>
                      The Client-Provided Scope of Work is comprehensive and accurate. It is assumed
                      that the costs associated with the SOW are reasonable and reflective of the
                      current market for labor and materials.
                    </Text>
                  </FlexRow>
                  <FlexRow alignItems="flex-start">
                    <Text style={fontStyles.body}>c.&nbsp;</Text>
                    <Text style={fontStyles.body}>
                      It is assumed that the renovation work will be completed in a workmanlike
                      manner and will meet all local building codes and zoning requirements.
                    </Text>
                  </FlexRow>
                </View>
              </View>,
              <View key="hypothetical-conditions">
                <Text style={fontStyles.body}>
                  <Text style={fontStyles.bodyBold}>Hypothetical Conditions:&nbsp;</Text>
                </Text>
                <View style={{ marginLeft: 12 }}>
                  <FlexRow alignItems="flex-start">
                    <Text style={fontStyles.body}>a.&nbsp;</Text>
                    <Text style={fontStyles.body}>
                      The After-Repair Value (ARV) is based on the Hypothetical Condition that all
                      repairs, renovations, and improvements detailed in the provided Scope of Work
                      have been completed as of the effective date of the appraisal. The property
                      does not currently exist in this state; the value is predicated on the future
                      completion of these specific tasks.
                    </Text>
                  </FlexRow>
                </View>
              </View>,
            ]}
          />
        </Tkv2PDFPage>
      </>
    );
  }

  const methodologyText = getMethodologyText(propertyType);

  return (
    <Tkv2PDFPage title={title} address={subjectAddress}>
      {methodologyText.map((text) => (
        <Text key={text} style={fontStyles.body}>
          {text}
        </Text>
      ))}

      {/* About This Report */}
      <Text style={fontStyles.h4}>About This Report</Text>
      <Text style={fontStyles.body}>
        This appraisal has been prepared and reported in accordance with the <Text style={fontStyles.bodyBold}>Uniform Standards of Professional Appraisal Practice (USPAP)</Text>, 2024 edition, under Standards Rule 2-2(b).
      </Text>
      <Text style={fontStyles.body}>
        The appraisal was developed through the <Text style={fontStyles.bodyBold}>Appraisify Hybrid Platform</Text>, which provides the appraiser with verified third-party inspection data, floor plans, photographs, Craftsman cost data, and paired-sales analytics.
      </Text>
      <Text style={fontStyles.body}>
        The appraiser did not perform a personal interior inspection. All physical characteristics, condition data, and photos were obtained from verified third-party sources.
      </Text>
      <Text style={fontStyles.body}>
        The <Text style={fontStyles.bodyBold}>Sales Comparison</Text> and <Text style={fontStyles.bodyBold}>Cost Approaches</Text> were developed; the <Text style={fontStyles.bodyBold}>Income Approach</Text> was excluded. Time adjustments were derived from <Text style={fontStyles.bodyBold}>Zillow HVI zip-code-level indices</Text>, and cost data from the <Text style={fontStyles.bodyBold}>Craftsman National Building Cost Guide</Text>.
      </Text>

      {/* Intended Use and Intended User */}
      <Text style={fontStyles.h4}>Intended Use and User</Text>
      <Text style={fontStyles.body}>
        The intended user is the <Text style={fontStyles.bodyBold}>client named in this report</Text> and its authorized assigns. Any other use by unintended parties is prohibited.
      </Text>
      <Text style={fontStyles.body}>
        The intended use of this report is for <Text style={fontStyles.bodyBold}>collateral evaluation, risk management, and credit decisioning</Text> by the client.
      </Text>

      {/* Definition of Market Value */}
      <View style={{ paddingRight: 24 }}>
        <Text style={[fontStyles.h4, { marginBottom: 10 }]}>Definition of Market Value</Text>
        <Text style={fontStyles.body}>
          The definition of Market Value conforms to <Text style={fontStyles.bodyBold}>12 CFR Part 34, Subpart C - Appraisals, §34.42(g)</Text> of the Office of the Comptroller of the Currency:
        </Text>
        <Text style={[fontStyles.bodyItalic, { marginBottom: 4 }]}>
          The most probable price that a property should bring in a competitive and open market under
          all conditions requisite to a fair sale with the buyer and seller each acting prudently and
          knowledgeably, and assuming the price is not affected by undue stimulus. Implicit in this
          definition is the consummation of a sale as of a specified date and the passing of title
          from seller to buyer under conditions whereby:
        </Text>

        <List
          type="bullet"
          items={[
            <Text key="buyer-seller-motivation" style={fontStyles.bodyItalic}>
              Buyer and seller are typically motivated;
            </Text>,
            <Text key="well-informed-parties" style={fontStyles.bodyItalic}>
              Both parties are well informed or well advised, and each acting in what he or she
              considers his or her own best interest;
            </Text>,
            <Text key="reasonable-exposure-time" style={fontStyles.bodyItalic}>
              A reasonable time is allowed for exposure in the open market;
            </Text>,
            <Text key="payment-terms" style={fontStyles.bodyItalic}>
              Payment is made in terms of cash in U.S. dollars or in terms of financial arrangements
              comparable thereto; and
            </Text>,
            <Text key="normal-consideration" style={fontStyles.bodyItalic}>
              The price represents the normal consideration for the property sold unaffected by
              special or creative financing or sales concessions granted by anyone associated with the
              sale.
            </Text>,
          ]}
        />
      </View>

      {/* Scope of Work and Reporting Process */}
      <View style={{ paddingRight: 24, marginTop: 24 }}>
        <Text style={[fontStyles.h4, { marginBottom: 10 }]}>Scope of Work and Reporting Process</Text>
        <List
          type="number"
          items={[
            <Text key="property-identification" style={fontStyles.body}>
              <Text style={fontStyles.bodyBold}>Property Identification -&nbsp;</Text>
              The subject was identified by street address, parcel number, and GIS mapping tools verified through the Appraisify platform.
            </Text>,
            <Text key="inspection" style={fontStyles.body}>
              <Text style={fontStyles.bodyBold}>Inspection -&nbsp;</Text>
              The appraiser relied on a third-party property data report (photo and sketch verified). No personal inspection was performed.
            </Text>,
            <Text key="data-sources" style={fontStyles.body}>
              <Text style={fontStyles.bodyBold}>Data Sources -&nbsp;</Text>
              Data were obtained from MLS, public record, tax assessor data, flood maps, surveys, satellite imagery, and proprietary Appraisify analytics.
            </Text>,
            <View key="approaches-to-value">
              <Text style={[fontStyles.body, { marginBottom: 4 }]}>
                <Text style={fontStyles.bodyBold}>Approaches to Value -&nbsp;</Text>
              </Text>
              <List
                type="bullet"
                items={[
                  <Text key="sales-comparison" style={fontStyles.body}>
                    <Text style={fontStyles.bodyBold}>Sales Comparison:&nbsp;</Text>
                    Primary method using paired-sales and regression analysis.
                  </Text>,
                  <Text key="cost-approach" style={fontStyles.body}>
                    <Text style={fontStyles.bodyBold}>Cost Approach:&nbsp;</Text>
                    Applied on all properties except condominiums using Craftsman cost data.
                  </Text>,
                  <Text key="income-approach" style={fontStyles.body}>
                    <Text style={fontStyles.bodyBold}>Income Approach:&nbsp;</Text>
                    Omitted as not applicable to typical market participants.
                  </Text>,
                ]}
              />
            </View>,
            <Text key="adjustments" style={fontStyles.body}>
              <Text style={fontStyles.bodyBold}>Adjustments -&nbsp;</Text>
              Time adjustments were applied using Zillow HVI trend data; location and physical adjustments were derived from paired-sales analysis.
            </Text>,
            <Text key="compliance" style={fontStyles.body}>
              <Text style={fontStyles.bodyBold}>Compliance -&nbsp;</Text>
              The appraiser certifies this report complies with <Text style={fontStyles.bodyBold}>USPAP, Appraiser Independence Requirements (AIR), and OCC Interagency Guidelines</Text>.
            </Text>
          ]}
        />
      </View>

      {/* Assumptions and Limiting Conditions */}
      <Text style={fontStyles.h4}>Statement of Assumptions and Limiting Conditions</Text>
      <Text style={fontStyles.body}>
        The appraiser assumes that the title is good and marketable unless he or she becomes aware of information to the contrary.
      </Text>
      <Text style={fontStyles.body}>
        If a sketch or floor plan is included, it illustrates approximate dimensions and is included only to assist the reader in visualizing the property and understanding the appraiser’s determination of its size.
      </Text>
      <Text style={fontStyles.body}>
        The appraiser has relied on data provided by third parties in this appraisal report. Such data may include, but is not limited to, floor plans, flood maps, multiple listing services, tax assessment records, public land records, satellite imagery, virtual street views, property data services, surveys, engineering reports, and data aggregations. After review of these sources, the appraiser used only the data considered reliable. The appraiser assumes there are no material omissions and makes no guarantees, express or implied, regarding their accuracy. If the source data predates the effective date of appraisal, it is assumed that property characteristics have not changed.
      </Text>
      <Text style={fontStyles.body}>
        The appraiser will not be required to give testimony or appear in court because of this appraisal unless specific arrangements have been made in advance or as required by law.
      </Text>
      <Text style={fontStyles.body}>
        The appraiser has noted any adverse conditions (such as needed repairs, deterioration, or the presence of hazardous materials) that became known during development of this appraisal. Unless otherwise stated, the appraiser has no knowledge of hidden or unapparent physical deficiencies or adverse environmental conditions that could affect value, and assumes none exist. The appraiser makes no guarantees or warranties, express or implied, regarding such matters. This report must not be considered an environmental assessment.
      </Text>

      {/* Assignment Conditions */}
      <Text style={fontStyles.h4}>Assignment Conditions</Text>
      <List
        type="bullet"
        items={[
          <Text key="interest-appraised" style={fontStyles.body}>
            <Text style={fontStyles.bodyBold}>Interest Appraised:&nbsp;</Text>
            Fee Simple Estate, unless otherwise stated.
          </Text>,
          <View style={gridStyles.column}>
            <Text key="extraordinary-assumptions" style={[fontStyles.body, { marginBottom: 4 }]}>
              <Text style={fontStyles.bodyBold}>Extraordinary Assumptions:</Text>
            </Text>
            <List
              type="number"
              items={[
                <Text key="physical-characteristics" style={fontStyles.body}>
                  The physical characteristics and condition of the property, as reported by the third-party inspection, are accurate.
                </Text>,
                <Text key="market-data" style={fontStyles.body}>
                  Market and cost data from the Appraisify platform are reliable and reflective of prevailing conditions.
                </Text>
              ]}
            />
          </View>,
          <Text key="hypothetical-conditions" style={fontStyles.body}>
            <Text style={fontStyles.bodyBold}>Hypothetical Conditions (if applicable):&nbsp;</Text>
            When an <Text style={fontStyles.bodyBold}>After-Repair Value (ARV)</Text> is reported, it is based on the hypothetical condition that all proposed renovations or repairs have been completed as of the effective date.
          </Text>,
        ]}
      />

      {/* Data Rights, Disclosure and Permitted Use */}
      <Text style={fontStyles.h4}>Data Rights, Disclosure and Permitted Use</Text>
      <Text style={fontStyles.body}>
        The appraiser acknowledges and agrees that Appraisify, its affiliates, and its clients (including but not limited to lenders, investors, regulators, and government-sponsored enterprises) may:
      </Text>
      <List
        type="number"
        items={[
          <Text key="store-use-copy" style={fontStyles.body}>
            <Text style={fontStyles.bodyBold}>Store, use, copy, transmit, analyze, reproduce, publish, or otherwise utilize</Text> the data, photographs, sketches, analytics, and other information contained in this appraisal report (collectively, “Appraisal Data”) for any lawful business, regulatory, analytic, or quality control purpose.
          </Text>,
          <Text key="incorporate" style={fontStyles.body}>
            Incorporate the Appraisal Data into <Text style={fontStyles.bodyBold}>automated valuation models (AVMs), machine-learning systems, market trend analyses, collateral monitoring tools, or other data products</Text> developed or maintained by Appraisify or its affiliates.
          </Text>,
          <Text key="derivative" style={fontStyles.body}>
            Create <Text style={fontStyles.bodyBold}>derivative data sets, anonymized data, or aggregated statistical products</Text> derived from the Appraisal Data, and to store, share, license, or distribute such derivative products without limitation.
          </Text>,
          <Text key="federal" style={fontStyles.body}>
            Share Appraisal Data with <Text style={fontStyles.bodyBold}>federal or state regulators, investors, data partners, or third-party analytics providers</Text> for purposes of market surveillance, regulatory compliance, portfolio risk analysis, and valuation research.
          </Text>,
          <Text key="perpetuity" style={fontStyles.body}>
            Retain Appraisal Data in <Text style={fontStyles.bodyBold}>perpetuity</Text>, in original and/or structured digital form, for purposes of compliance, audit, data analysis, and product improvement.
          </Text>,
        ]}
      />
      <Text style={fontStyles.body}>
        Nothing in this section shall be construed to make any recipient of this report an “intended user” as defined by USPAP, nor does it grant the right to alter the value opinion, analyses, or conclusions expressed in this report.
      </Text>
      <Text style={fontStyles.body}>
        The appraiser recognizes that Appraisify and its clients may store and process this Appraisal Data through cloud-based systems and agrees that such use does <Text style={fontStyles.bodyBold}>not constitute a breach of confidentiality</Text> under USPAP when used in accordance with these stated purposes.
      </Text>

      {/* Limitation of Liability */}
      <Text style={fontStyles.h4}>Limitation of Liability</Text>
      <Text style={fontStyles.body}>
        This appraisal was prepared for the stated intended use and intended user only. Any unauthorized use or alteration of this report is prohibited.
      </Text>
      <Text style={fontStyles.body}>
        Neither Appraisify nor the appraiser shall have liability for any use of this report or its data that is outside the scope of its intended purpose, or for derivative uses of the Appraisal Data consistent with the terms above.
      </Text>

      {/* Time Adjustment Method Using ZHVI */}
      <Text style={fontStyles.h4}>Time Adjustment Method Using ZHVI</Text>
      <Text style={fontStyles.body}>
        <Text style={fontStyles.bodyBold}>What ZHVI is.&nbsp;</Text>
        ZHVI represents the **typical** home value for a region, built from monthly changes in
        property-level Zestimates and commonly published as a smoothed, seasonally adjusted series
        that captures the middle tier of the local price distribution.
      </Text>
      <Text style={fontStyles.body}>
        <Text style={fontStyles.bodyBold}>How it&apos;s applied.&nbsp;</Text>
        The appraiser selects the narrowest relevant geography, computes the monthly rate of change
        for the purposes of applying time adjustments. ZHVI is a market-level indicator and may not
        fully capture subject-specific dynamics.
      </Text>

      {/* Third Party Inspection Disclosure */}
      <Text style={fontStyles.h4}>Third Party Inspection Disclosure</Text>
      <Text style={fontStyles.body}>
        Inspectify is a non-licensed third-party vendor engaged by the client to collect photos and
        observable facts. The vendor did not develop a value opinion and is not acting as an
        appraiser. Any opinions by the vendor are not incorporated as appraisal opinions. The
        appraiser independently evaluated the facts reported.
      </Text>

      {/* AMC Fee Disclosure */}
      <Text style={fontStyles.h4}>AMC Fee Disclosure</Text>
      <Text style={fontStyles.body}>
        For this assignment, the appraiser was paid {schAloftPrice.parse(appraiserFee).toString()}
        &nbsp;for appraisal services. Appraisify received an AMC fee of&nbsp;
        {schAloftPrice.parse(amcFee).toString()} for administrative, technology, and quality control
        services related to this order. These amounts are separate, and the appraiser&apos;s
        compensation was not contingent on the value opinion or any loan decision.
      </Text>

      {/* Data and Model Disclosures */}
      <Text style={fontStyles.h4}>Data and Model Disclosures</Text>
      <Text style={fontStyles.body}>
        <Text style={fontStyles.bodyBold}>Appraisify Toolkit.&nbsp;</Text>
        The order was originated, analyzed, and delivered through Appraisify Toolkit. Platform tools
        organize information and support the appraiser&apos;s work. The appraiser remains
        responsible for all analyses and conclusions.
      </Text>
      <Text style={fontStyles.body}>
        <Text style={fontStyles.bodyBold}>
          Craftsman cost data and Paired Cost Framework.&nbsp;
        </Text>
        Craftsman references were used to estimate replacement cost new and local multipliers and to
        derive physical depreciation and site-value extractions for selected comparables. These
        internal calculations supported adjustments and the Paired Cost Framework. A formal Cost
        Approach is not reported. Workfile retains the calculations.
      </Text>
      <Text style={fontStyles.body}>
        <Text style={fontStyles.bodyBold}>ZHVI.&nbsp;</Text>
        ZHVI is a smoothed, seasonally adjusted monthly series designed to reflect the typical home
        value for the middle tier of properties in the selected region. It is used as a directional
        indicator of market trend and reconciled to micro-market evidence.
      </Text>

      {/* Disclaimers */}
      <Text style={fontStyles.h4}>Disclaimers</Text>
      <List
        type="bullet"
        items={[
          <Text key="not-inspection" style={fontStyles.body}>
            No inspection by appraiser. Unseen conditions could affect value. This uncertainty is
            reflected in the scope and reconciliation.
          </Text>,
          <Text key="data-furnished" style={fontStyles.body}>
            Data furnished by others. Third-party and public data are assumed reliable but not
            guaranteed. The appraiser does not assume responsibility for the accuracy of items
            furnished by others.
          </Text>,
          <Text key="distribution" style={fontStyles.body}>
            Distribution. Any distribution must include the complete report with all statements and
            conditions.
          </Text>,
        ]}
      />
    </Tkv2PDFPage >
  );
};
